import React, { useState, useEffect } from "react";
import { Button, FormGroup, Spinner, Label, Input, Form } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { useFormInputValidation } from "react-form-input-validation";
import { Toast } from "utility/helpers";
import {
  getAccessToken,
  setUserDetails,
  setUserPermissions,
  setAccessToken,
  setPassCode,
} from "config";
import {
  ACCOUNT_COMMITTEE,
  ACCOUNT_OTHERS,
  CODE_LOGIN_PERMISSIONS,
  congRoles,
} from "utility/constants";
import ToggleSwitch from "components/customs/toggle-switch";
import styled from "styled-components";
import PasswordInput from "components/customs/password-input/PasswordInput";
import { codeLogin } from "services/services.accounts";
import useAuth from "hooks/useAuth";

const LoginCardHeader = styled.div`
  margin-bottom: 24px;

  h1 {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.black900};
  }
`;

const StyledFromGroup = styled(FormGroup)`
  margin-bottom: 16px !important;

  label {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.black800};
    font-size: 12px;
  }

  input {
    height: 48px;
    background: ${({ theme }) => theme.colors.black50};
    border-color: ${({ theme }) => theme.colors.black100};
    font-size: 15px;
    line-height: 20px;

    &:focus {
      box-shadow: none;
      border-color: ${({ theme }) => theme.colors.systemBlue};

      ~ .input-group-text {
        background: white;
        border-color: ${({ theme }) => theme.colors.systemBlue};
        border-width: 1px;
      }
    }
  }

  .input-group {
    input {
      border-width: 1px;
      border-radius: 0.375rem 0 0 0.375rem;
    }
  }

  .input-group-text {
    background: ${({ theme }) => theme.colors.black50};
    border-color: ${({ theme }) => theme.colors.black100};
  }
`;

const ButtonSection = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 32px;
  margin-bottom: 32px;
`;

const DividerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 16px;
  margin-bottom: 16px;

  .divider-text {
    color: ${({ theme }) => theme.colors.black800};
    font-size: 14px;
    margin: 8px 0;
  }

  .switch-link {
    color: ${({ theme }) => theme.colors.systemBlue};
    font-size: 14px;
    cursor: pointer;
    text-decoration: none;
    background: none;
    border: none;
    padding: 0;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SubmitButton = styled(Button)`
  height: 53px;
  width: 100%;
  background: ${({ theme }) => theme.colors.secondaryBlue};
  font-weight: 700;
  font-size: 18.3673px;
  line-height: 24.4898px;
`;

const ToggleSwitchSection = styled(StyledFromGroup)`
  margin-bottom: 20px !important;

  .toggle-switch-container {
    .toggle-switch-label {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.black900};
      line-height: 20px;
      font-weight: 400;
    }

    .toggle-switch-slider {
      background: ${({ theme }) => theme.colors.black100};
      border-color: ${({ theme }) => theme.colors.black50};
    }

    .toggle-switch-input:focus + .toggle-switch-slider {
      box-shadow: none;
    }

    .toggle-switch-input:checked + .toggle-switch-slider {
      background: ${({ theme }) => theme.colors.systemBlue};
    }
  }
`;

const LoginForm = ({ loginMode = "regular", setLoginMode }) => {
  const [fields, errors, form] = useFormInputValidation(
    {
      email: "",
      password: "",
      code: "",
    },
    {
      email: "required|email",
      password: "required",
      code: "required",
    }
  );

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const navigate = useNavigate();
  const { signIn, getAuthenticatedUser } = useAuth();

  // Function to handle toggle change
  const handleToggleChange = (e) => {
    setRememberMe(e.target.checked);
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    localStorage.clear();
    setIsLoggingIn(!isLoggingIn);

    // Validate only relevant fields based on login mode
    let isValid = false;

    if (loginMode === "code") {
      // Validate code field only
      if (!fields.code || fields.code.trim() === "") {
        Toast({
          type: 4,
          content: "Code is required",
        });
        isValid = false;
        setIsLoggingIn(false);
      } else {
        isValid = true;
      }
    } else {
      // Validate email and password fields
      isValid = form.validate(e);
    }

    if (isValid) {
      if (loginMode === "code") {
        // Handle code login using the codeLogin service
        codeLogin({
          passCode: fields.code,
          callback: (result) => {
            if (result.isSuccess && result.token) {
              // Store the token
              setAccessToken(result.token);

              // Store the passCode in localStorage for use in Others page
              setPassCode(fields.code);

              // Get permissions and user details
              setUserPermissions(CODE_LOGIN_PERMISSIONS);
              setUserDetails({ type: ACCOUNT_OTHERS });

              setTimeout(() => {
                setIsLoggingIn(false);
                window.location.href = "/dpwhlisting";
              }, 1500);
            } else {
              setIsLoggingIn(false);

              Toast({
                type: 4,
                content: result.msg || "Code login failed",
              });
            }
          },
        });
      } else {
        // Clear passCode on regular login to avoid confusion
        setPassCode(null, true);

        signIn(fields.email, fields.password)
          .then(async (user) => {
            const userDetails = await getAuthenticatedUser(
              user?.signInUserSession?.accessToken?.jwtToken
            );

            const cong = userDetails?.congressman;
            const accountType = userDetails?.type;

            setTimeout(() => {
              if (accountType && congRoles.includes(accountType)) {
                // eslint-disable-next-line max-len
                window.location.href = `/representatives/${cong.region.id}/cong/${cong.id}`;
              } else if (accountType === ACCOUNT_COMMITTEE) {
                window.location.href = "/committee";
              } else {
                window.location = "/";
              }
            }, 1500);
          })
          .catch((Exception) => {
            setIsLoggingIn(false);

            Toast({
              type: 4,
              content: Exception?.message,
            });
          });
      }
    }
  };

  useEffect(() => {
    // if user has user token, redirect to home
    if (window.location.pathname === "/login" && getAccessToken() !== null) {
      navigate("/home");
    }
  }, []);

  const prevLoginModeRef = React.useRef(loginMode);

  useEffect(() => {
    // Only run when loginMode actually changes (not on initial mount)
    if (prevLoginModeRef.current === loginMode) {
      return;
    }

    prevLoginModeRef.current = loginMode;

    // Skip if form methods are not available
    if (!form || typeof form.handleChangeEvent !== "function") {
      return;
    }

    // Clear form errors if method exists
    if (typeof form.setErrors === "function") {
      form.setErrors({});
    }
  }, [loginMode]);

  const handleSwitchMode = (mode) => {
    setLoginMode(mode);
  };

  return (
    <Form
      onSubmit={handleOnSubmit}
      className="col-10 col-sm-6 col-lg-9"
    >
      <LoginCardHeader>
        <h1>{"User Login"}</h1>
      </LoginCardHeader>

      {loginMode === "regular" ? (
        <>
          <StyledFromGroup>
            <Label>{"Login"}</Label>

            <Input
              type="email"
              name="email"
              placeholder="Email"
              autoComplete={rememberMe ? "username" : "off"}
              onBlur={form.handleBlurEvent}
              onChange={form.handleChangeEvent}
            />

            <small className="error text-danger">
              {errors.email ? errors.email : ""}
            </small>
          </StyledFromGroup>
          <StyledFromGroup>
            <Label>{"Password"}</Label>
            <PasswordInput
              type="password"
              name="password"
              placeholder="Enter password"
              autoComplete={rememberMe ? "current-password" : "off"}
              onBlur={form.handleBlurEvent}
              onChange={form.handleChangeEvent}
            />

            <small className="error text-danger">
              {errors.password ? errors.password : ""}
            </small>
          </StyledFromGroup>

          <ToggleSwitchSection>
            <ToggleSwitch
              id="rememberMe"
              name="rememberMe"
              checked={rememberMe}
              onChange={handleToggleChange}
              label="Remember me"
            />
          </ToggleSwitchSection>

          <ButtonSection>
            <SubmitButton type="submit">
              {isLoggingIn ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Login"
              )}
            </SubmitButton>
          </ButtonSection>

          <DividerSection>
            <span className="divider-text">{"or"}</span>
            <button
              type="button"
              className="switch-link"
              onClick={() => handleSwitchMode("code")}
            >
              {"Login by Code"}
            </button>
          </DividerSection>
        </>
      ) : (
        <>
          <StyledFromGroup>
            <Label>{"Code"}</Label>

            <Input
              type="text"
              name="code"
              placeholder="Enter code"
              autoComplete="one-time-code"
              onBlur={form.handleBlurEvent}
              onChange={form.handleChangeEvent}
            />

            <small className="error text-danger">
              {errors.code ? errors.code : ""}
            </small>
          </StyledFromGroup>

          <ButtonSection>
            <SubmitButton type="submit">
              {isLoggingIn ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Login"
              )}
            </SubmitButton>
          </ButtonSection>

          <DividerSection>
            <span className="divider-text">{"or"}</span>
            <button
              type="button"
              className="switch-link"
              onClick={() => handleSwitchMode("regular")}
            >
              {"Login with Email"}
            </button>
          </DividerSection>
        </>
      )}
    </Form>
  );
};

export default LoginForm;
